// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Group, Modal, Select, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { createReference, formatHumanName, normalizeErrorString } from '@medplum/core';
import type { Encounter, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { PATIENT_EVALUATION_STATUS, POLARIS_EVALUATION_TAG_SYSTEM } from '@polarissleep/shared';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { withTenantScope } from '../../utils/tenant';

interface OrderHSATModalProps {
  opened: boolean;
  onClose: () => void;
  onPatientSelected: (patient: Patient, encounter: Encounter) => Promise<void>;
}

export function OrderHSATModal({ opened, onClose, onPatientSelected }: OrderHSATModalProps): JSX.Element {
  const medplum = useMedplum();
  const [patients, setPatients] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [ordering, setOrdering] = useState(false);

  const fetchApprovedPatients = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const results = await medplum.searchResources('Patient', {
        _tag: `${POLARIS_EVALUATION_TAG_SYSTEM}|${PATIENT_EVALUATION_STATUS.approved}`,
        _count: '200',
        _fields: 'id,name',
      });

      setPatients(
        results
          .filter((p): p is Patient & { id: string } => Boolean(p.id))
          .map((p) => ({
            value: p.id,
            label: p.name?.[0] ? formatHumanName(p.name[0]) : 'Unknown Patient',
          }))
      );
    } finally {
      setLoading(false);
    }
  }, [medplum]);

  useEffect(() => {
    if (opened) {
      fetchApprovedPatients().catch(console.error);
    } else {
      setSelectedPatientId(null);
    }
  }, [opened, fetchApprovedPatients]);

  const handleOrder = async (): Promise<void> => {
    if (!selectedPatientId) {
      return;
    }
    setOrdering(true);
    try {
      const patient = await medplum.readResource('Patient', selectedPatientId);
      const encounters = await medplum.searchResources('Encounter', {
        subject: `Patient/${selectedPatientId}`,
        _count: '100',
      });
      const latestEncounter = encounters
        .filter((encounter): encounter is Encounter & { id: string } => Boolean(encounter.id))
        .sort((a, b) => (b.period?.start ?? '').localeCompare(a.period?.start ?? ''))[0];
      const encounter =
        latestEncounter ??
        (await medplum.createResource(
          withTenantScope<Encounter>({
            resourceType: 'Encounter',
            status: 'in-progress',
            class: {
              system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
              code: 'AMB',
              display: 'ambulatory',
            },
            subject: createReference(patient),
            period: { start: new Date().toISOString() },
          })
        ));
      onClose();
      await onPatientSelected(patient, encounter);
    } catch (err) {
      showNotification({ color: 'red', title: 'Error', message: normalizeErrorString(err) });
    } finally {
      setOrdering(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Order HSAT" centered>
      <Stack gap="md">
        <Select
          label="Patient"
          placeholder="Select an approved patient"
          data={patients}
          value={selectedPatientId}
          onChange={setSelectedPatientId}
          searchable
          disabled={loading || ordering}
          nothingFoundMessage={loading ? 'Loading...' : 'No approved patients found'}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="outline" style={{ borderColor: '#640040', color: '#640040' }} onClick={onClose}>
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: '#640040', color: 'white' }}
            disabled={!selectedPatientId || ordering}
            loading={ordering}
            onClick={() => {
              handleOrder().catch(console.error);
            }}
          >
            Order
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
